import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

function severityTone(s) {
  if (s === "Critical") return "var(--cv-crimson)";
  if (s === "High") return "#D55816";
  if (s === "Medium") return "#E5C243";
  return "var(--cv-slate)";
}

function providerBadge(p) {
  if (p === "AWS")   return { bg: "rgba(213,88,22,0.14)", fg: "#D55816" };
  if (p === "Azure") return { bg: "rgba(74,90,106,0.12)", fg: "var(--cv-slate)" };
  if (p === "GCP")   return { bg: "rgba(62,124,79,0.14)", fg: "var(--cv-green)" };
  return { bg: "var(--cv-line)", fg: "var(--cv-charcoal)" };
}

export default function CloudSecurity() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { apiGet("/cloud/summary").then(setD); }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;
  const postureTone = d.posture_score >= 85 ? "var(--cv-green)"
    : d.posture_score >= 70 ? "#D55816" : "var(--cv-crimson)";

  return (
    <>
      <Masthead
        module="cloud"
        eyebrow="Cloud Security · CSPM · CWPP · CIEM · DSPM"
        title="Cloud Security Posture"
        subtitle="Multi-cloud posture · misconfigs · attack paths · workload protection · identity & data security"
        meta={<div>{k.accounts_total} accounts · {fmtNum(k.workloads_total)} workloads<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">

        {/* Hero row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <button type="button" data-testid="cloud-posture"
                  onClick={() => nav("/drill/cloud_compliance_controls")}
                  className="cv-panel text-left hover:shadow-lg transition-shadow"
                  style={{ borderLeft: `4px solid ${postureTone}`, cursor: "pointer" }}>
            <div className="cv-kpi-label">Cloud Security Posture</div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: postureTone, fontSize: 60 }}>
                {d.posture_score}
              </div>
              <div className="pb-2">
                <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {d.posture_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of CSPM misconfigs, CWPP runtime findings, attack paths, CIEM &amp; DSPM controls.
            </div>
          </button>
          <Kpi testid="cloud-accounts" label="Cloud accounts / subscriptions" value={k.accounts_total}
               sub={`${k.accounts_aws} AWS · ${k.accounts_azure} Azure · ${k.accounts_gcp} GCP`}
               onClick={() => nav("/drill/cloud_accounts")} />
          <Kpi testid="cloud-workloads" label="Cloud workloads" value={fmtNum(k.workloads_total)}
               sub={`${fmtNum(k.workloads_vm)} VMs · ${fmtNum(k.workloads_container)} pods · ${fmtNum(k.workloads_serverless)} fns · ${fmtNum(k.workloads_db)} DBs · ${fmtNum(k.workloads_storage)} buckets`}
               onClick={() => nav("/drill/cloud_workloads")} />
          <Kpi testid="cloud-attack-paths" label="Attack paths · toxic combos" value={k.attack_paths}
               sub={`${k.attack_paths_critical} critical · ${k.attack_paths_high} high · ${k.attack_paths_medium} medium`}
               alert
               trend={{ delta_pct: +3, direction: "up", period: "WoW", is_improving: false }}
               onClick={() => nav("/drill/cloud_attack_paths?severity=Critical")} />
        </div>

        {/* CSPM · Misconfigurations */}
        <div className="cv-section-label">CSPM · Misconfigurations by Severity</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="cloud-miscfg-critical" label="Critical misconfigs" value={fmtNum(k.miscfg_critical)}
               sub="open · needs immediate remediation" alert
               onClick={() => nav("/drill/cloud_misconfigs?severity=Critical")} />
          <Kpi testid="cloud-miscfg-high" label="High-severity misconfigs" value={fmtNum(k.miscfg_high)} sub="open findings"
               onClick={() => nav("/drill/cloud_misconfigs?severity=High")} />
          <Kpi testid="cloud-public" label="Publicly exposed assets" value={fmtNum(k.public_assets)}
               sub="internet-reachable resources" alert
               onClick={() => nav("/drill/cloud_misconfigs?category_key=network&severity=Critical")} />
          <Kpi testid="cloud-runtime" label="Runtime threats (24h)" value={k.runtime_threats_24h}
               sub={`${k.runtime_threats_critical} critical · ${k.runtime_threats_high} high · ${k.runtime_threats_medium} medium`}
               alert
               onClick={() => nav("/drill/cloud_workloads?exposed_publicly=true")} />
        </div>

        {/* CWPP · Kubernetes + Runtime */}
        <div className="cv-section-label">CWPP · Workload Protection · Kubernetes</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="cloud-k8s-clusters" label="Kubernetes clusters" value={k.k8s_clusters}
               sub="EKS · AKS · GKE"
               onClick={() => nav("/drill/cloud_workloads?kind_key=container")} />
          <Kpi testid="cloud-k8s-images" label="Vulnerable container images" value={fmtNum(k.k8s_vulnerable_images)}
               sub="images with critical CVEs" alert
               onClick={() => nav("/drill/cloud_workloads?kind_key=container")} />
          <Kpi testid="cloud-k8s-admission" label="Admission policy violations (24h)" value={fmtNum(k.k8s_admission_violations_24h)}
               sub="blocked or warned by OPA / Gatekeeper"
               onClick={() => nav("/drill/cloud_workloads?kind_key=container")} />
          <Kpi testid="cloud-dspm" label="Sensitive data stores (DSPM)" value={k.sensitive_buckets}
               sub={`${k.unencrypted_pii_stores} unencrypted PII stores`}
               alert
               onClick={() => nav("/drill/cloud_misconfigs?category_key=storage")} />
        </div>

        {/* Row: Accounts + Attack paths + Misconfig by category */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-cloud-accounts" title="Multi-cloud Coverage"
                 subtitle="Accounts &amp; subscriptions across providers">
            <div className="grid grid-cols-3 gap-2 mb-3">
              {d.accounts_by_provider.map((p) => {
                const b = providerBadge(p.provider);
                return (
                  <button type="button" key={p.provider}
                          data-testid={`cloud-provider-${p.provider.toLowerCase()}`}
                          onClick={() => nav(`/drill/cloud_accounts?provider=${p.provider}`)}
                          className="cv-tile text-left hover:shadow-md transition-shadow"
                          style={{ cursor: "pointer" }}>
                    <div className="cv-tile-label" style={{ color: b.fg }}>{p.provider}</div>
                    <div className="cv-tile-value">{p.count}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>
                      posture avg <strong style={{ color: "var(--cv-charcoal)" }}>{p.posture_avg}</strong>
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--cv-crimson)" }}>
                      {p.critical_findings} critical findings
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Top-risk accounts
            </div>
            <div className="flex flex-col gap-1.5">
              {d.top_accounts.map((a) => (
                <button type="button" key={a.key}
                        data-testid={`cloud-acct-${a.key}`}
                        onClick={() => nav(`/drill/cloud_misconfigs?account=${encodeURIComponent(a.key)}`)}
                        className="flex justify-between items-center gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{a.key}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{a.provider} · {a.purpose}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif-display tabular-nums text-[16px]" style={{ color: severityTone(a.sev) }}>{a.score}</div>
                    <div className="text-[10.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>{a.critical} crit</div>
                  </div>
                </button>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-attack-paths" title="Toxic Combinations · Attack Paths"
                 subtitle="Multi-step paths from a public entry to a crown-jewel asset"
                 unit="count" current={k.attack_paths}>
            <div className="flex flex-col gap-1.5">
              {d.top_attack_paths.map((p) => (
                <button type="button" key={p.path_id}
                        data-testid={`attack-path-${p.path_id}`}
                        onClick={() => nav(`/drill/cloud_attack_paths?severity=${p.severity}`)}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{p.path_id}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>{p.path}</div>
                    <div className="text-[10.5px] mt-0.5" style={{ color: "var(--cv-charcoal-soft)" }}>{p.account}</div>
                  </div>
                  <span className="text-[10.5px] small-caps font-bold shrink-0 mt-0.5" style={{ color: severityTone(p.severity) }}>{p.severity}</span>
                </button>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-miscfg-cats" title="Misconfigurations by Category"
                 subtitle="Where cloud posture debt sits">
            <div className="flex flex-col gap-2">
              {d.misc_by_category.map((c) => {
                const max = Math.max(...d.misc_by_category.map(x => x.count));
                const pct = Math.round((c.count / max) * 100);
                const trendTone = c.trend_pct >= 0 ? "var(--cv-crimson)" : "var(--cv-green)";
                return (
                  <button type="button" key={c.category}
                          data-testid={`miscfg-cat-${c.key}`}
                          onClick={() => nav(`/drill/cloud_misconfigs?category_key=${c.key}`)}
                          className="text-left w-full py-1 hover:opacity-80 transition-opacity"
                          style={{ background: "transparent", border: 0, cursor: "pointer" }}>
                    <div className="flex justify-between items-center text-[13px] mb-0.5">
                      <span className="truncate pr-2" style={{ color: "var(--cv-charcoal)" }}>{c.category}</span>
                      <span className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>
                        {fmtNum(c.count)} <span className="text-[11px] font-normal" style={{ color: trendTone }}>({c.trend_pct >= 0 ? "+" : ""}{c.trend_pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--cv-crimson), #D55816)" }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>

        {/* Row: K8s clusters + Public assets + Data findings */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-k8s" title="Kubernetes Clusters"
                 subtitle={`${d.k8s.clusters.length} clusters · EKS · AKS · GKE`}>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">Cluster</th>
                    <th className="py-2 px-2 font-bold">Provider</th>
                    <th className="py-2 px-2 font-bold text-right">Nodes</th>
                    <th className="py-2 px-2 font-bold text-right">Ver.</th>
                    <th className="py-2 px-2 font-bold text-right">Crit img</th>
                    <th className="py-2 px-2 font-bold text-right">Adm viol.</th>
                  </tr>
                </thead>
                <tbody>
                  {d.k8s.clusters.map((c) => (
                    <tr key={c.cluster} data-testid={`k8s-cluster-${c.cluster}`}
                        onClick={() => nav(`/drill/cloud_workloads?kind_key=container&provider=${c.provider}`)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{c.cluster}</td>
                      <td className="py-2 px-2" style={{ color: providerBadge(c.provider).fg }}>{c.provider}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.nodes}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.version}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-bold" style={{ color: c.critical_images >= 10 ? "var(--cv-crimson)" : (c.critical_images >= 5 ? "#D55816" : "var(--cv-charcoal)") }}>{c.critical_images}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{c.admission_violations_24h}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-public-assets" title="Top Publicly Exposed Assets"
                 subtitle="Internet-facing resources with critical/high findings"
                 unit="count" current={k.public_assets}>
            <div className="flex flex-col gap-1.5">
              {d.top_public_assets.map((a) => (
                <button type="button" key={a.resource}
                        data-testid={`public-asset-${a.resource.replace(/[^a-zA-Z0-9]/g,'-')}`}
                        onClick={() => nav(`/drill/cloud_misconfigs?account=${encodeURIComponent(a.account)}`)}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate" style={{ color: "var(--cv-charcoal)" }}>{a.resource}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{a.kind} · {a.account}</div>
                  </div>
                  <span className="text-[10.5px] small-caps font-bold shrink-0" style={{ color: severityTone(a.severity) }}>{a.severity}</span>
                </button>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-dspm" title="DSPM · Sensitive Data Findings"
                 subtitle={`${k.sensitive_buckets} sensitive stores · ${k.unencrypted_pii_stores} unencrypted PII`}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button type="button" onClick={() => nav("/drill/cloud_misconfigs?category_key=storage&severity=Critical")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Sensitive data stores</div>
                <div className="cv-tile-value">{k.sensitive_buckets}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_misconfigs?category_key=encryption&severity=Critical")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Unencrypted PII stores</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{k.unencrypted_pii_stores}</div>
              </button>
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Top data-security findings
            </div>
            <div className="flex flex-col gap-1.5">
              {d.dspm.top_data_findings.map((f) => (
                <button type="button" key={f.resource}
                        onClick={() => nav("/drill/cloud_misconfigs?category_key=encryption")}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate" style={{ color: "var(--cv-charcoal)" }}>{f.resource}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{f.kind} · {f.issue}</div>
                  </div>
                  <span className="text-[10.5px] small-caps font-bold shrink-0" style={{ color: severityTone(f.severity) }}>{f.severity}</span>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        {/* Row: CIEM + Severity bar chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-ciem" title="CIEM · Cloud Identities"
                 subtitle="Cloud entitlements &amp; least-privilege posture"
                 unit="count" current={k.identities_total}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button type="button" onClick={() => nav("/drill/cloud_identities")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Total cloud identities</div>
                <div className="cv-tile-value">{fmtNum(k.identities_total)}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_identities?overprivileged=true")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Overprivileged</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{fmtNum(k.identities_overprivileged)}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_identities?risk=Medium")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Dormant &gt; 90d</div>
                <div className="cv-tile-value" style={{ color: "#D55816" }}>{fmtNum(k.identities_dormant_90d)}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_identities?risk=High")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Privileged w/o MFA</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{fmtNum(k.identities_no_mfa_priv)}</div>
              </button>
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>
              Overprivileged rate ·{" "}
              <strong style={{ color: "var(--cv-charcoal)" }}>
                {((k.identities_overprivileged / k.identities_total) * 100).toFixed(1)}%
              </strong>{" "}
              of cloud identities have permissions they haven't used in 90 days.
            </div>
          </Panel>

          <Panel testid="panel-severity" title="Findings by Severity"
                 subtitle="Open cloud misconfigurations">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { severity: "Critical", value: k.miscfg_critical, fill: "#A5300F" },
                    { severity: "High",     value: k.miscfg_high,     fill: "#D55816" },
                    { severity: "Medium",   value: k.miscfg_medium,   fill: "#E5C243" },
                    { severity: "Low",      value: k.miscfg_low,      fill: "#4A5A6A" },
                  ]}
                  margin={{ top: 8, right: 12, bottom: 0, left: -12 }}
                  onClick={(e) => {
                    if (e && e.activeLabel) nav(`/drill/cloud_misconfigs?severity=${e.activeLabel}`);
                  }}
                >
                  <CartesianGrid stroke="var(--cv-line)" strokeDasharray="3 4" vertical={false} />
                  <XAxis dataKey="severity" tick={{ fill: "var(--cv-dark-grey)", fontSize: 12 }} stroke="var(--cv-line)" />
                  <YAxis tick={{ fill: "var(--cv-dark-grey)", fontSize: 11 }} stroke="var(--cv-line)" />
                  <Tooltip />
                  <Bar dataKey="value" isAnimationActive={false} style={{ cursor: "pointer" }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11.5px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>
              Click a bar to drill into that severity.
            </div>
          </Panel>

          <Panel testid="panel-runtime" title="Runtime Threats (24h)"
                 subtitle="CWPP detections across workloads"
                 unit="count" current={k.runtime_threats_24h}>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button type="button" onClick={() => nav("/drill/cloud_workloads?exposed_publicly=true")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Critical</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{k.runtime_threats_critical}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_workloads")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">High</div>
                <div className="cv-tile-value" style={{ color: "#D55816" }}>{k.runtime_threats_high}</div>
              </button>
              <button type="button" onClick={() => nav("/drill/cloud_workloads")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Medium</div>
                <div className="cv-tile-value" style={{ color: "#E5C243" }}>{k.runtime_threats_medium}</div>
              </button>
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Signals include
            </div>
            <ul className="text-[12.5px] leading-[1.55]" style={{ color: "var(--cv-charcoal)" }}>
              <li>· Container escape attempts &amp; suspicious syscalls</li>
              <li>· Crypto-mining detected on VMs</li>
              <li>· Anomalous access to secrets manager</li>
              <li>· Unexpected outbound to blocklisted IP</li>
            </ul>
          </Panel>
        </div>

        {/* Compliance Frameworks */}
        <div className="mb-5">
          <Panel testid="panel-cloud-compliance" title="Cloud Compliance Frameworks"
                 subtitle="Control coverage across cloud &amp; regulatory standards"
                 unit="percent"
                 current={Math.round(d.compliance.frameworks.reduce((s, f) => s + f.coverage, 0) / d.compliance.frameworks.length)}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {d.compliance.frameworks.map((f) => {
                const tone = f.coverage >= 85 ? "var(--cv-green)" : f.coverage >= 65 ? "#D55816" : "var(--cv-crimson)";
                return (
                  <button type="button" key={f.name}
                          data-testid={`cloud-framework-${f.key}`}
                          onClick={() => nav(`/drill/cloud_compliance_controls?framework_key=${f.key}`)}
                          className="rounded-xl p-3 text-left hover:shadow-md transition-shadow"
                          style={{ background: "var(--cv-sand)", border: "1px solid var(--cv-line)", cursor: "pointer" }}>
                    <div className="text-[12px] font-bold small-caps tracking-[0.08em] leading-snug min-h-[32px]" style={{ color: "var(--cv-charcoal)" }}>
                      {f.name}
                    </div>
                    <div className="flex items-end gap-2 mt-2">
                      <div className="font-serif-display leading-none text-[30px]" style={{ color: tone }}>{f.coverage}%</div>
                      <div className="text-[11px] mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>target {f.target}%</div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(50,50,50,0.08)" }}>
                      <div className="h-full rounded-full" style={{ width: `${f.coverage}%`, background: tone }} />
                    </div>
                    <div className="text-[11.5px] mt-1.5" style={{ color: "var(--cv-charcoal-soft)" }}>
                      {f.controls_met} of {f.controls_total} controls met
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>

      </div>
    </>
  );
}
